"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

const services = [
    {
        title: "Search Engine Optimization",
        tag: "SEO",
        description: "Building sustainable visibility where search intent begins.",
        href: "/services/search-visibility/",
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
                <circle cx="28" cy="28" r="16" stroke="#3730a3" strokeWidth="3.5" fill="none"/>
                <circle cx="28" cy="28" r="9" stroke="#4f46e5" strokeWidth="2" fill="none" strokeDasharray="4 3"/>
                <line x1="39" y1="39" x2="54" y2="54" stroke="#3730a3" strokeWidth="4" strokeLinecap="round"/>
                <circle cx="28" cy="28" r="3" fill="#4f46e5"/>
                <motion.circle cx="28" cy="28" r="16" stroke="#818cf8" strokeWidth="2" fill="none"
                    strokeDasharray="6 4"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                    style={{ transformOrigin: "28px 28px" }}
                />
            </svg>
        ),
        wide: false,
    },
    {
        title: "Social Media Marketing",
        tag: "SMM",
        description: "Turning attention into engagement across all social platforms.",
        href: "/services/social-media/",
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
                <circle cx="14" cy="32" r="7" stroke="#3730a3" strokeWidth="3" fill="none"/>
                <circle cx="50" cy="14" r="7" stroke="#4f46e5" strokeWidth="3" fill="none"/>
                <circle cx="50" cy="50" r="7" stroke="#4f46e5" strokeWidth="3" fill="none"/>
                <line x1="21" y1="29" x2="43" y2="17" stroke="#818cf8" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="21" y1="35" x2="43" y2="47" stroke="#818cf8" strokeWidth="2.5" strokeLinecap="round"/>
                <motion.circle cx="50" cy="14" r="3.5" fill="#4f46e5"
                    animate={{ scale: [1, 1.4, 1] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                />
                <motion.circle cx="50" cy="50" r="3.5" fill="#4f46e5"
                    animate={{ scale: [1, 1.4, 1] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.7, ease: "easeInOut" }}
                />
                <circle cx="14" cy="32" r="3.5" fill="#3730a3"/>
            </svg>
        ),
        wide: false,
    },
    {
        title: "Website Design & Development",
        tag: "Web Dev",
        description: "Designing digital experiences built for performance and scale.",
        href: "/services/web-development/",
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
                <rect x="6" y="10" width="52" height="36" rx="5" stroke="#3730a3" strokeWidth="3" fill="none"/>
                <line x1="6" y1="20" x2="58" y2="20" stroke="#4f46e5" strokeWidth="2"/>
                <circle cx="13" cy="15" r="2" fill="#4f46e5"/>
                <circle cx="20" cy="15" r="2" fill="#818cf8"/>
                <circle cx="27" cy="15" r="2" fill="#a5b4fc"/>
                <line x1="22" y1="54" x2="42" y2="54" stroke="#3730a3" strokeWidth="3" strokeLinecap="round"/>
                <line x1="32" y1="46" x2="32" y2="54" stroke="#3730a3" strokeWidth="2.5"/>
                <motion.rect x="14" y="28" width="12" height="3" rx="1.5" fill="#4f46e5"
                    animate={{ width: [12, 20, 12] }}
                    transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                />
                <rect x="14" y="34" width="20" height="3" rx="1.5" fill="#818cf8" opacity="0.6"/>
                <rect x="36" y="28" width="14" height="10" rx="3" stroke="#4f46e5" strokeWidth="2" fill="none"/>
            </svg>
        ),
        wide: false,
    },
    {
        title: "Performance Marketing",
        tag: "Ads",
        description: "Driving measurable growth through precision-targeted campaigns.",
        href: "/services/performance-marketing/",
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
                <polyline points="6,50 18,34 28,40 40,22 54,14" stroke="#3730a3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <motion.circle cx="54" cy="14" r="5" fill="#4f46e5"
                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                />
                <circle cx="40" cy="22" r="3.5" fill="#818cf8"/>
                <circle cx="28" cy="40" r="3" fill="#a5b4fc"/>
                <line x1="6" y1="56" x2="58" y2="56" stroke="#e5e7eb" strokeWidth="1.5"/>
                <line x1="6" y1="10" x2="6" y2="56" stroke="#e5e7eb" strokeWidth="1.5"/>
                <motion.line x1="6" y1="50" x2="54" y2="14" stroke="#818cf8" strokeWidth="1" strokeDasharray="3 3" opacity="0.4"
                    animate={{ strokeDashoffset: [0, -12] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                />
            </svg>
        ),
        wide: false,
    },
    {
        title: "Brand & Creative Strategy",
        tag: "Branding",
        description: "Defining clear brand direction and creative foundations that drive consistency across every touchpoint.",
        href: "/services/brand-strategy/",
        icon: (
            <svg viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-20 h-16">
                <path d="M40 8 L54 32 L40 56 L26 32 Z" stroke="#3730a3" strokeWidth="3" fill="none" strokeLinejoin="round"/>
                <path d="M40 18 L48 32 L40 46 L32 32 Z" fill="#4f46e5" opacity="0.15"/>
                <circle cx="40" cy="32" r="5" fill="#4f46e5"/>
                <motion.circle cx="40" cy="32" r="14" stroke="#818cf8" strokeWidth="1.5" fill="none" strokeDasharray="5 3"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
                    style={{ transformOrigin: "40px 32px" }}
                />
                <circle cx="40" cy="8" r="3" fill="#a5b4fc"/>
                <circle cx="40" cy="56" r="3" fill="#a5b4fc"/>
                <circle cx="54" cy="32" r="3" fill="#a5b4fc"/>
                <circle cx="26" cy="32" r="3" fill="#a5b4fc"/>
            </svg>
        ),
        wide: true,
    },
    {
        title: "Data & Analytics",
        tag: "Analytics",
        description: "Turning raw data into revenue decisions — tracking what actually moves growth forward.",
        href: "/services/performance-marketing/marketing-analytics/",
        icon: (
            <svg viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-20 h-16">
                <motion.rect x="10" y="38" width="12" height="18" rx="3" fill="#4f46e5"
                    animate={{ height: [18, 28, 18], y: [38, 28, 38] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                />
                <motion.rect x="30" y="26" width="12" height="30" rx="3" fill="#3730a3"
                    animate={{ height: [30, 22, 30], y: [26, 34, 26] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.4, ease: "easeInOut" }}
                />
                <motion.rect x="50" y="14" width="12" height="42" rx="3" fill="#818cf8"
                    animate={{ height: [42, 50, 42], y: [14, 6, 14] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.8, ease: "easeInOut" }}
                />
                <line x1="6" y1="58" x2="68" y2="58" stroke="#e5e7eb" strokeWidth="1.5"/>
            </svg>
        ),
        wide: true,
    },
];

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
    }),
} as import('framer-motion').Variants;

export default function ServiceRotatingCards() {
    return (
        <section className="w-full bg-white py-24 md:py-32" data-theme="light">
            <div className="max-w-[90rem] mx-auto px-6 md:px-12">

                {/* Header */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-4xl md:text-7xl font-bold tracking-tight mb-4" style={{ color: "#18181b" }}>
                        Our <span style={{ color: "#3730a3" }}>Services</span>
                    </h2>
                    <p className="text-base md:text-xl text-zinc-500 max-w-2xl mx-auto">
                        We specialise in building visibility, engineering growth, and designing experiences that convert.
                    </p>
                </motion.div>

                {/* Top 4 grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
                    {services.filter(s => !s.wide).map((service, i) => (
                        <motion.div
                            key={service.title}
                            custom={i}
                            variants={cardVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="group bg-zinc-50 hover:bg-indigo-50 border border-zinc-200 hover:border-indigo-200 rounded-3xl p-7 flex flex-col gap-4 transition-all duration-300 cursor-pointer"
                        >
                            <div className="h-20 flex items-center">
                                {service.icon}
                            </div>
                            <div>
                                <span className="text-xs font-mono tracking-widest text-indigo-500 uppercase">{service.tag}</span>
                                <h3 className="text-lg font-bold mt-1 text-zinc-800 group-hover:text-indigo-800 transition-colors leading-snug">
                                    {service.title}
                                </h3>
                            </div>
                            <p className="text-zinc-500 text-sm leading-relaxed flex-1">
                                {service.description}
                            </p>
                            <Link
                                href={service.href}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 group-hover:gap-3 transition-all duration-300"
                            >
                                Explore Service <ArrowRight size={15} />
                            </Link>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom 2 wide cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
                    {services.filter(s => s.wide).map((service, i) => (
                        <motion.div
                            key={service.title}
                            custom={i + 4}
                            variants={cardVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="group bg-zinc-50 hover:bg-indigo-50 border border-zinc-200 hover:border-indigo-200 rounded-3xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 transition-all duration-300 cursor-pointer"
                        >
                            <div className="shrink-0">
                                {service.icon}
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="text-xs font-mono tracking-widest text-indigo-500 uppercase">{service.tag}</span>
                                <h3 className="text-xl font-bold text-zinc-800 group-hover:text-indigo-800 transition-colors leading-snug">
                                    {service.title}
                                </h3>
                                <p className="text-zinc-500 text-sm leading-relaxed">
                                    {service.description}
                                </p>
                                <Link
                                    href={service.href}
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 group-hover:gap-3 transition-all duration-300 mt-1"
                                >
                                    Explore Service <ArrowRight size={15} />
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    className="flex justify-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <Link
                        href="/services"
                        className="group relative inline-flex items-center gap-3 border border-indigo-200 text-zinc-800 px-10 py-4 rounded-full font-bold overflow-hidden transition-all duration-500 hover:border-indigo-600 hover:shadow-[0_0_40px_rgba(79,70,229,0.15)]"
                    >
                        <div className="absolute inset-0 bg-indigo-600 translate-y-full transition-transform duration-500 ease-out group-hover:translate-y-0" />
                        <span className="relative z-10 flex items-center gap-2 uppercase tracking-wide text-sm transition-colors duration-500 group-hover:text-white">
                            View All Services <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
                        </span>
                    </Link>
                </motion.div>

            </div>
        </section>
    );
}
